import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { API_BASE } from '../lib/api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { InlineError } from '../components/InlineError'
import '../styles/ui.css'
import '../styles/never-out.css'

// ── API types ─────────────────────────────────────────────────────────

type NoStore = {
    storeId: string
    storeNumber: string
    name: string
    city: string | null
    itemCount: number
    alertCount: number
    enabled: boolean | null
    nextRunAt: string | null
    lastError: string | null
}

type NoItem = {
    id: string
    storeId: string
    itemCardId: number
    colorId: number
    sizeId: number
    description: string
    styleCode: string | null
    prodNo: string | null
    brand: string | null
    colorName: string | null
    sizeName: string | null
    scope: 'Variant' | 'ProductTotal'
    minQty: number
    maxQty: number | null
    isActive: boolean
    status: 'Ok' | 'BelowMin' | 'AboveMax'
    lastQuantity: number
    lastCheckedAt: string | null
    breachedAt: string | null
    lastNotifiedAt: string | null
    notifyCount: number
    isSilenced: boolean
    silencedBy: string | null
    currentQuantity: number | null
}

type NoItemsResponse = { liveError: boolean; items: NoItem[] }

type NoSearchResult = {
    itemCardId: number
    description: string
    styleCode: string | null
    prodNo: string | null
    brand: string | null
    storeQuantity: number
}

type NoVariant = {
    colorId: number
    sizeId: number
    colorName: string | null
    sizeName: string | null
    chainQuantity: number
    storeQuantities: Record<string, number>
    monitoredIn: string[]
}

type NoContact = { id?: string; email: string; userId: string | null; displayName: string | null }

type NoSettings = {
    enabled: boolean
    scheduleKind: 'Daily' | 'Weekly' | 'EveryHours'
    dailyTime: string
    weeklyDay: number
    intervalHours: number
    renotifyHours: number
    nextRunAt: string | null
    lastRunAt: string | null
    lastError: string | null
}

type NoSettingsResponse = {
    settings: NoSettings; contacts: NoContact[]
    serverTimeZone: string | null; serverTimeZoneName: string | null; serverUtcOffsetMinutes: number | null
}

type NoLogEntry = { id: string; kind: string; detail: string; recipients: string | null; itemCount: number; createdAt: string }


const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const STORE_SEL_KEY = 'neverout-store-sel'

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

// Human label for the server's timezone, noting when it differs from the browser's.
const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return null } })()
const serverTzLabel = (name: string | null, id: string | null, serverOffsetMin: number | null | undefined) => {
    const label = name || id || "the server's local time zone"
    // JS getTimezoneOffset is minutes behind UTC (inverted), so negate to get the true offset.
    const browserOffset = -new Date().getTimezoneOffset()
    if (browserTz && serverOffsetMin != null && serverOffsetMin !== browserOffset)
        return `${label} — different from your browser (${browserTz})`
    return label
}

// Legacy placeholder colour/size values on non-grid items ("-", "Not Applicable", "NA"…).
const isPlaceholderName = (s: string | null | undefined) => {
    const t = (s ?? '').trim().toLowerCase()
    return t === '' || t === '-' || t === 'na' || t === 'n/a' || t === 'not applicable'
}

/** Human variant label with legacy placeholders stripped; empty string for non-grid items. */
const variantLabel = (colorName: string | null, sizeName: string | null) =>
    [colorName, sizeName].filter(s => !isPlaceholderName(s)).join(' / ')

type SortKey = 'product' | 'brand' | 'watched' | 'store' | 'onhand' | 'min' | 'max' | 'status'

// One product at one store, collapsing all of its monitored variant/total lines into a single
// watch-list row. Per-variant thresholds are edited in the row-click popup.
type ProductGroup = {
    key: string
    storeId: string
    itemCardId: number
    description: string
    styleCode: string | null
    prodNo: string | null
    brand: string | null
    seed: NoItem
    items: NoItem[]
    watchedVariants: number          // active variant-scope lines
    hasTotal: boolean                // an active product-total line exists
    alarmCount: number               // active lines currently in alarm (variant + total)
    onHand: number | null            // product-total on-hand (or summed watched variants)
    totalMin: number | null          // product-total min, if a product-total line is monitored
    totalMax: number | null
    anyActive: boolean
    status: 'Ok' | 'BelowMin' | 'AboveMax' | 'Unknown'
    alarmedIds: string[]
    silencedAlarmedIds: string[]
    allAlarmsSilenced: boolean
}

// Size-aware column ordering for the colour/size grid: numeric sizes ascending, then the
// standard letter run (XXS…4XL), then anything else alphabetically.
// Arrow-key navigation between grid cells (Puresoft4-style quick entry): inputs carry
// data-r/data-c; arrows jump to the nearest input in that direction, Enter submits.
const gridKeyNav = (e: React.KeyboardEvent, onEnter?: () => void) => {
    const t = e.target as HTMLInputElement
    if (!(t instanceof HTMLInputElement) || !t.classList.contains('no-grid-input')) return
    if (e.key === 'Enter') { e.preventDefault(); onEnter?.(); return }
    const dirs: Record<string, [number, number]> = {
        ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
    }
    const d = dirs[e.key]
    if (!d) return
    const r = parseInt(t.dataset.r ?? '', 10)
    const c = parseInt(t.dataset.c ?? '', 10)
    const table = t.closest('table')
    if (isNaN(r) || isNaN(c) || !table) return
    let best: HTMLInputElement | null = null
    let bestDist = Infinity
    for (const el of table.querySelectorAll<HTMLInputElement>('input.no-grid-input')) {
        const er = parseInt(el.dataset.r ?? '', 10)
        const ec = parseInt(el.dataset.c ?? '', 10)
        if (isNaN(er) || isNaN(ec)) continue
        // Vertical: same column, nearest row; horizontal: same row, nearest column.
        const dist = d[0] !== 0
            ? (ec === c ? (er - r) * d[0] : -1)
            : (er === r ? (ec - c) * d[1] : -1)
        if (dist > 0 && dist < bestDist) { best = el; bestDist = dist }
    }
    if (best) { e.preventDefault(); best.focus(); best.select() }
}

// Number field for a threshold where blank = "not monitored". Custom up/down chevrons step
// 3 → 2 → 1 → 0 → blank, with the down chevron disabled at blank (native spinners are hidden).
function StepInput({ value, onChange, placeholder, dataR, dataC, width }: {
    value: string
    onChange: (v: string) => void
    placeholder?: string
    dataR?: number
    dataC?: number
    width?: number
}) {
    const isBlank = value.trim() === ''
    const stepDown = () => {
        if (isBlank) return
        const n = parseInt(value, 10)
        onChange(isNaN(n) || n <= 0 ? '' : String(n - 1))
    }
    const stepUp = () => {
        if (isBlank) { onChange('0'); return }
        const n = parseInt(value, 10)
        onChange(String((isNaN(n) ? 0 : n) + 1))
    }
    return (
        <span className="no-step" style={width ? { width } : undefined}>
            <input className="no-grid-input" type="number" min={0} placeholder={placeholder ?? ''}
                data-r={dataR} data-c={dataC}
                value={value} onChange={e => onChange(e.target.value)} />
            <span className="no-step-btns">
                <button type="button" tabIndex={-1} aria-label="Increase" onClick={stepUp}>▲</button>
                <button type="button" tabIndex={-1} aria-label="Decrease" onClick={stepDown} disabled={isBlank}>▼</button>
            </span>
        </span>
    )
}

const LETTER_SIZES = ['xxs', 'xs', 's', 'sm', 'm', 'md', 'l', 'lg', 'xl', 'xxl', '2xl', 'xxxl', '3xl', '4xl']
const sizeCompare = (a: string, b: string) => {
    const na = parseFloat(a), nb = parseFloat(b)
    const aNum = !isNaN(na), bNum = !isNaN(nb)
    if (aNum && bNum) return na - nb
    if (aNum !== bNum) return aNum ? -1 : 1
    const ra = LETTER_SIZES.indexOf(a.trim().toLowerCase())
    const rb = LETTER_SIZES.indexOf(b.trim().toLowerCase())
    if (ra !== -1 && rb !== -1) return ra - rb
    if ((ra !== -1) !== (rb !== -1)) return ra !== -1 ? -1 : 1
    return a.localeCompare(b)
}

// ── Page ──────────────────────────────────────────────────────────────

export default function NeverOutPage() {
    const token = useAuthStore(s => s.accessToken)
    const user = useAuthStore(s => s.user)
    const canManage = user?.permissions?.includes('neverout.manage') ?? false
    const qc = useQueryClient()
    const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token])

    // Store scope: a single store id, or 'all' for every permitted store (dashboard-style dropdown).
    const [storeSel, setStoreSel] = useState<string>(() => localStorage.getItem(STORE_SEL_KEY) ?? '')
    const [addOpen, setAddOpen] = useState(false)
    const [copyOpen, setCopyOpen] = useState(false)
    const [settingsStore, setSettingsStore] = useState<NoStore | null>(null)
    const [filter, setFilter] = useState('')
    const [alarmsOnly, setAlarmsOnly] = useState(false)
    const [sortKey, setSortKey] = useState<SortKey>('product')
    const [sortDir, setSortDir] = useState<1 | -1>(1)

    // The open edit dialog is URL-addressable (?edit={itemId}) so email deep links land on
    // the right SKU and a refresh mid-edit reopens the dialog — same idiom as /admin/roadmap.
    const [searchParams, setSearchParams] = useSearchParams()
    const editId = searchParams.get('edit')
    const [editItem, setEditItem] = useState<NoItem | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)

    const openEdit = (item: NoItem) => {
        setEditItem(item)
        setSearchParams(p => { const n = new URLSearchParams(p); n.set('edit', item.id); return n })
    }
    const closeEdit = () => {
        setEditItem(null)
        setSearchParams(p => { const n = new URLSearchParams(p); n.delete('edit'); return n }, { replace: true })
    }

    useEffect(() => {
        if (storeSel) localStorage.setItem(STORE_SEL_KEY, storeSel)
    }, [storeSel])

    const { data: stores, isLoading: storesLoading, error: storesError } = useQuery<NoStore[]>({
        queryKey: ['neverout-stores'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/neverout/stores', { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    // Default/repair the dropdown selection once stores load.
    useEffect(() => {
        if (!stores || stores.length === 0) return
        setStoreSel(prev => {
            if (prev === 'all' && stores.length > 1) return prev
            if (stores.some(s => s.storeId === prev)) return prev
            return stores.length === 1 ? stores[0].storeId : 'all'
        })
    }, [stores])

    const selected = useMemo(() => {
        if (!stores) return []
        if (storeSel === 'all') return stores.map(s => s.storeId)
        return stores.some(s => s.storeId === storeSel) ? [storeSel] : []
    }, [stores, storeSel])

    const storeIdsParam = selected.join(',')
    const { data: itemsData, isLoading: itemsLoading } = useQuery<NoItemsResponse>({
        queryKey: ['neverout-items', storeIdsParam],
        enabled: selected.length > 0,
        queryFn: async () => {
            const res = await fetch(API_BASE + `/api/neverout/items?storeIds=${storeIdsParam}`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    const storeName = (id: string) =>
        stores?.find(s => s.storeId.toLowerCase() === id.toLowerCase())?.name ?? id

    const refreshItems = () => {
        qc.invalidateQueries({ queryKey: ['neverout-items'] })
        qc.invalidateQueries({ queryKey: ['neverout-stores'] })
        // Refresh the product editor's data too, so reopening shows updated thresholds + audit info.
        qc.invalidateQueries({ queryKey: ['neverout-product'] })
    }

    // Product-level silence: flips every alarmed variant line of a product in one go (there's no
    // single "product" row in the DB — each variant is its own monitor). Fires the per-item PATCH
    // that individual rows used, batched across the group's alarmed lines.
    const silenceGroup = useMutation({
        mutationFn: async ({ ids, silence }: { ids: string[]; silence: boolean }) => {
            await Promise.all(ids.map(async id => {
                const res = await fetch(API_BASE + `/api/neverout/items/${id}`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ silence }),
                })
                if (!res.ok) {
                    const json = await res.json().catch(() => ({}))
                    throw new Error(json.error ?? 'Failed to update.')
                }
            }))
        },
        onSuccess: refreshItems,
        onError: (e: Error) => setActionError(e.message),
    })

    const items = useMemo(() => itemsData?.items ?? [], [itemsData])

    // Displayed on-hand: live quantity when available, else the last checked value.
    const onHandOf = (i: NoItem): number | null =>
        i.currentQuantity ?? (i.lastCheckedAt ? i.lastQuantity : null)
    // Status from the LIVE on-hand (not the last scheduled check's persisted value), so the pill,
    // row highlight, and counts all agree with the On hand / Min / Max columns.
    const liveStatus = (i: NoItem): 'Ok' | 'BelowMin' | 'AboveMax' | 'Unknown' => {
        const q = onHandOf(i)
        if (q === null) return 'Unknown'
        if (q < i.minQty) return 'BelowMin'
        if (i.maxQty !== null && q > i.maxQty) return 'AboveMax'
        return 'Ok'
    }
    const isAlert = (i: NoItem) => {
        const s = liveStatus(i)
        return i.isActive && (s === 'BelowMin' || s === 'AboveMax')
    }

    // Live alarm count per store (for the store picker), from the loaded items.
    const liveAlertsByStore = useMemo(() => {
        const m = new Map<string, number>()
        for (const i of items) if (isAlert(i)) m.set(i.storeId.toLowerCase(), (m.get(i.storeId.toLowerCase()) ?? 0) + 1)
        return m
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items])

    // One row per product per store: collapse the per-variant monitor rows into a single group
    // keyed by (store, item card). The variant detail lives in the row-click popup editor.
    const groups = useMemo<ProductGroup[]>(() => {
        const map = new Map<string, NoItem[]>()
        for (const i of items) {
            const k = `${i.storeId.toLowerCase()}:${i.itemCardId}`
            const arr = map.get(k)
            if (arr) arr.push(i); else map.set(k, [i])
        }
        const out: ProductGroup[] = []
        for (const [key, list] of map) {
            const seed = list[0]
            const totalItem = list.find(i => i.scope === 'ProductTotal')
            const variantItems = list.filter(i => i.scope === 'Variant')
            const active = list.filter(i => i.isActive)
            const alarmed = list.filter(isAlert)
            // Product on-hand: the true full-product total when a product-total monitor exists
            // (the backend sums every variant), otherwise the sum of the watched variant lines.
            let onHand: number | null
            if (totalItem && onHandOf(totalItem) !== null) {
                onHand = onHandOf(totalItem)
            } else {
                const vals = variantItems.map(onHandOf).filter((n): n is number => n !== null)
                onHand = vals.length ? vals.reduce((a, b) => a + b, 0) : null
            }
            let status: ProductGroup['status'] = 'Unknown'
            if (active.some(i => liveStatus(i) === 'BelowMin')) status = 'BelowMin'
            else if (active.some(i => liveStatus(i) === 'AboveMax')) status = 'AboveMax'
            else if (active.some(i => liveStatus(i) === 'Ok')) status = 'Ok'
            const silencedAlarmed = alarmed.filter(i => i.isSilenced)
            out.push({
                key, storeId: seed.storeId, itemCardId: seed.itemCardId,
                description: seed.description, styleCode: seed.styleCode, prodNo: seed.prodNo, brand: seed.brand,
                seed, items: list,
                watchedVariants: variantItems.filter(i => i.isActive).length,
                hasTotal: !!totalItem && totalItem.isActive,
                alarmCount: alarmed.length,
                onHand,
                totalMin: totalItem ? totalItem.minQty : null,
                totalMax: totalItem ? totalItem.maxQty : null,
                anyActive: active.length > 0,
                status,
                alarmedIds: alarmed.map(i => i.id),
                silencedAlarmedIds: silencedAlarmed.map(i => i.id),
                allAlarmsSilenced: alarmed.length > 0 && silencedAlarmed.length === alarmed.length,
            })
        }
        return out
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items])

    const alarmProductCount = groups.filter(g => g.alarmCount > 0).length

    // Toolbar filter (+ "Alarms only") + column-click sorting, over the product groups.
    const visibleGroups = useMemo(() => {
        const q = filter.trim().toLowerCase()
        let list = q
            ? groups.filter(g =>
                [g.description, g.styleCode, g.prodNo, g.brand, storeName(g.storeId)]
                    .some(v => (v ?? '').toLowerCase().includes(q)))
            : groups
        if (alarmsOnly) list = list.filter(g => g.alarmCount > 0)
        // Numeric compare with nulls always sorted to the end, whichever the direction.
        const numCmp = (a: number | null, b: number | null) => {
            if (a === null && b === null) return 0
            if (a === null) return sortDir
            if (b === null) return -sortDir
            return a - b
        }
        const statusRank = (g: ProductGroup) =>
            !g.anyActive ? 0 : g.status === 'BelowMin' ? 3 : g.status === 'AboveMax' ? 2 : 1
        const str = (v: string | null | undefined) => (v ?? '').toLowerCase()
        const cmp: Record<SortKey, (a: ProductGroup, b: ProductGroup) => number> = {
            product: (a, b) => str(a.description).localeCompare(str(b.description)),
            brand: (a, b) => str(a.brand).localeCompare(str(b.brand)),
            watched: (a, b) => (a.alarmCount - b.alarmCount) || (a.watchedVariants - b.watchedVariants),
            store: (a, b) => str(storeName(a.storeId)).localeCompare(str(storeName(b.storeId))),
            onhand: (a, b) => numCmp(a.onHand, b.onHand),
            min: (a, b) => numCmp(a.totalMin, b.totalMin),
            max: (a, b) => numCmp(a.totalMax, b.totalMax),
            status: (a, b) => statusRank(a) - statusRank(b),
        }
        return [...list].sort((a, b) => {
            const d = sortDir * cmp[sortKey](a, b)
            return d !== 0 ? d : str(a.description).localeCompare(str(b.description))
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, filter, alarmsOnly, sortKey, sortDir, stores])

    const sortBy = (key: SortKey) => {
        if (sortKey === key) setSortDir(d => (d === 1 ? -1 : 1))
        else { setSortKey(key); setSortDir(1) }
    }
    const SortTh = ({ k, children, right }: { k: SortKey; children: React.ReactNode; right?: boolean }) => (
        <th className="no-sortable" style={right ? { textAlign: 'right' } : undefined} onClick={() => sortBy(k)}>
            {children}{sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}
        </th>
    )

    // Resolve ?edit={id} to an item: from the loaded list when possible, otherwise fetch it
    // directly — an email deep link may target a store that isn't in the current selection.
    useEffect(() => {
        if (!editId) { setEditItem(null); return }
        if (editItem?.id === editId) return
        const local = items.find(i => i.id === editId)
        if (local) { setEditItem(local); return }
        if (itemsLoading) return
        let cancelled = false
        fetch(API_BASE + `/api/neverout/items/${editId}`, { headers: authHeaders })
            .then(r => (r.ok ? r.json() : null))
            .then((item: NoItem | null) => {
                if (cancelled) return
                if (!item) { closeEdit(); return }
                // Deep-linked item may be in a store outside the current dropdown scope.
                setStoreSel(prev => (prev === 'all' ? prev : item.storeId))
                setEditItem(item)
            })
            .catch(() => { if (!cancelled) closeEdit() })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId, items, itemsLoading])

    // "Watched" cell text: how many variant lines (and whether the product-total) are monitored.
    const watchedSummary = (g: ProductGroup) => {
        if (!g.anyActive) return `${g.items.length} line${g.items.length === 1 ? '' : 's'} (paused)`
        if (g.watchedVariants === 0 && g.hasTotal) return 'Product total'
        return `${g.watchedVariants} variant${g.watchedVariants === 1 ? '' : 's'}${g.hasTotal ? ' + total' : ''}`
    }

    // The product pill reflects the LIVE on-hand vs thresholds across the group's lines (matching
    // the On hand / Min columns and the row highlight), not just the last scheduled check. When
    // multiple lines are in alarm it summarises the count; a single alarm names its kind.
    const groupStatusPill = (g: ProductGroup) => {
        if (!g.anyActive) return <span className="no-pill no-pill-unchecked">Paused</span>
        if (g.alarmCount > 0) {
            const kindLabel = g.status === 'AboveMax' ? 'Overstock' : 'Below min'
            const label = g.alarmCount === 1 ? kindLabel : `${g.alarmCount} alarms`
            if (g.allAlarmsSilenced)
                return <span className="no-pill no-pill-silenced" title="Every alarm on this product is silenced">🔕 {label} (silenced)</span>
            return <span className={`no-pill ${g.status === 'AboveMax' ? 'no-pill-above' : 'no-pill-below'}`}>{label}</span>
        }
        if (g.status === 'Unknown') return <span className="no-pill no-pill-unchecked">Not checked yet</span>
        return <span className="no-pill no-pill-ok">OK</span>
    }

    return (
        <>
            <div className="page-header">
                <h1>Never Out</h1>
                <p>Monitor must-have SKUs per store and get notified before they run out (or pile up).</p>
            </div>

            {actionError && (
                <InlineError message={actionError} onDismiss={() => setActionError(null)} style={{ marginBottom: 'var(--space-4)' }} />
            )}

            {storesLoading ? (
                <div className="empty-state"><p>Loading stores…</p></div>
            ) : storesError ? (
                <div className="empty-state">
                    <p>Couldn't load your stores. {(storesError as Error).message || 'Please try again.'}</p>
                </div>
            ) : !stores || stores.length === 0 ? (
                <div className="empty-state">
                    <p>You don't have access to any stores. Request store access from your profile.</p>
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-5)' }}>
                        <select
                            className="header-store-select"
                            value={storeSel}
                            onChange={e => setStoreSel(e.target.value)}
                        >
                            {stores.map(s => {
                                // Loaded stores use the live alert count (matches the pills); others
                                // fall back to the last scheduled check's count from the backend.
                                const alerts = selected.includes(s.storeId)
                                    ? (liveAlertsByStore.get(s.storeId.toLowerCase()) ?? 0)
                                    : s.alertCount
                                return (
                                    <option key={s.storeId} value={s.storeId}>
                                        {s.lastError ? '⚠ ' : ''}{s.name} ({s.itemCount}{alerts > 0 ? `, ${alerts} alarm${alerts === 1 ? '' : 's'}` : ''})
                                    </option>
                                )
                            })}
                            {stores.length > 1 && <option value="all">All My Stores</option>}
                        </select>
                        {canManage && (
                            <button
                                className="btn btn-secondary"
                                disabled={storeSel === 'all'}
                                title={storeSel === 'all'
                                    ? 'Pick a specific store to edit its notification settings'
                                    : 'Schedule, nag interval, and contacts for this store'}
                                onClick={() => {
                                    const s = stores.find(x => x.storeId === storeSel)
                                    if (s) setSettingsStore(s)
                                }}
                            >⚙️ Store settings</button>
                        )}
                        {canManage && stores.length > 1 && (
                            <button
                                className="btn btn-secondary"
                                disabled={storeSel === 'all'}
                                title={storeSel === 'all'
                                    ? "Pick a specific store to copy its SKUs from"
                                    : "Copy this store's monitored SKUs to another store"}
                                onClick={() => setCopyOpen(true)}
                            >⧉ Copy to store…</button>
                        )}
                        {(() => {
                            const errored = stores.filter(s => selected.includes(s.storeId) && s.lastError)
                            return errored.length > 0 ? (
                                <span className="no-inline-note" style={{ color: '#b3261e' }}
                                      title={errored.map(s => `${s.name}: ${s.lastError}`).join('\n')}>
                                    ⚠ last check failed for {errored.length === 1 ? errored[0].name : `${errored.length} stores`}
                                </span>
                            ) : null
                        })()}
                    </div>

                    {selected.length === 0 ? (
                        <div className="empty-state"><p>Select a store above to see its monitored SKUs.</p></div>
                    ) : (
                        <div className="data-table-wrapper">
                            <div className="data-table-toolbar">
                                <input
                                    className="search-input"
                                    style={{ maxWidth: 240 }}
                                    placeholder="Filter by product, brand, code…"
                                    value={filter}
                                    onChange={e => setFilter(e.target.value)}
                                />
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                    {groups.length} product{groups.length === 1 ? '' : 's'} · {items.length} variant{items.length === 1 ? '' : 's'}
                                    {(filter.trim() !== '' || alarmsOnly) && <> · showing {visibleGroups.length}</>}
                                    {alarmProductCount > 0 && <> · <strong style={{ color: '#b3261e' }}>{alarmProductCount} in alarm</strong></>}
                                    {itemsData?.liveError && <> · <span style={{ color: '#9a6b00' }}>⚠ live quantities unavailable</span></>}
                                </span>
                                <button
                                    className={`btn ${alarmsOnly ? 'btn-primary' : 'btn-secondary'}`}
                                    style={{ marginLeft: 'auto' }}
                                    aria-pressed={alarmsOnly}
                                    disabled={alarmProductCount === 0 && !alarmsOnly}
                                    title={alarmProductCount === 0 && !alarmsOnly
                                        ? 'No products are in alarm'
                                        : 'Show only products with one or more variants in alarm'}
                                    onClick={() => setAlarmsOnly(v => !v)}
                                >{alarmsOnly ? '🔔 Alarms only' : 'Alarms only'}</button>
                                {canManage && (
                                    <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ Add SKUs</button>
                                )}
                            </div>

                            {itemsLoading ? (
                                <div className="empty-state"><p>Loading…</p></div>
                            ) : items.length === 0 ? (
                                <div className="empty-state">
                                    <p>No SKUs monitored yet{canManage ? ' — click "Add SKUs" to get started.' : '.'}</p>
                                </div>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <SortTh k="product">Product</SortTh>
                                            <SortTh k="brand">Brand</SortTh>
                                            <SortTh k="watched">Watched</SortTh>
                                            {selected.length > 1 && <SortTh k="store">Store</SortTh>}
                                            <SortTh k="onhand" right>On hand</SortTh>
                                            <SortTh k="min" right>Min</SortTh>
                                            <SortTh k="max" right>Max</SortTh>
                                            <SortTh k="status">Status</SortTh>
                                            {canManage && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleGroups.length === 0 && (
                                            <tr><td colSpan={7 + (selected.length > 1 ? 1 : 0) + (canManage ? 1 : 0)}
                                                    style={{ textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                                                {alarmsOnly ? 'No products are in alarm.' : `No monitored products match "${filter}".`}
                                            </td></tr>
                                        )}
                                        {visibleGroups.map(g => (
                                            <tr key={g.key}
                                                className={`no-row-click ${g.anyActive && g.status === 'BelowMin' ? 'no-row-breach' : ''}`}
                                                style={{ opacity: g.anyActive ? 1 : 0.55, cursor: 'pointer' }}
                                                onClick={() => openEdit(g.seed)}
                                                title="Open this product">
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>{g.description || `Item ${g.itemCardId}`}</div>
                                                    {(g.styleCode || g.prodNo) && (
                                                        <div className="no-inline-note">
                                                            {[g.styleCode, g.prodNo && g.prodNo !== g.styleCode ? g.prodNo : null]
                                                                .filter(Boolean).join(' · ')}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{g.brand ?? '—'}</td>
                                                <td>
                                                    <div>{watchedSummary(g)}</div>
                                                    {g.alarmCount > 0 && (
                                                        <div className="no-inline-note" style={{ color: '#b3261e' }}>
                                                            {g.alarmCount} in alarm
                                                        </div>
                                                    )}
                                                </td>
                                                {selected.length > 1 && <td>{storeName(g.storeId)}</td>}
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{g.onHand ?? '—'}</td>
                                                <td style={{ textAlign: 'right' }}
                                                    title={g.totalMin === null ? 'Monitored per variant — open to see each line' : undefined}>
                                                    {g.totalMin ?? '—'}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>{g.totalMax ?? '—'}</td>
                                                <td>{groupStatusPill(g)}</td>
                                                {canManage && (
                                                    <td onClick={e => e.stopPropagation()}>
                                                        <div className="table-actions">
                                                            {g.alarmCount > 0 && (
                                                                <button
                                                                    className="btn-icon"
                                                                    disabled={silenceGroup.isPending}
                                                                    title={g.allAlarmsSilenced ? 'Resume alarms for this product' : 'Silence alarms for this product'}
                                                                    onClick={() => silenceGroup.mutate({
                                                                        ids: g.allAlarmsSilenced
                                                                            ? g.silencedAlarmedIds
                                                                            : g.alarmedIds.filter(id => !g.silencedAlarmedIds.includes(id)),
                                                                        silence: !g.allAlarmsSilenced,
                                                                    })}
                                                                >{g.allAlarmsSilenced ? '🔔' : '🔕'}</button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </>
            )}

            {addOpen && (
                <AddSkusModal
                    storeIds={selected}
                    storeName={storeName}
                    authHeaders={authHeaders}
                    onClose={() => setAddOpen(false)}
                    onAdded={() => { setAddOpen(false); refreshItems() }}
                />
            )}

            {copyOpen && storeSel !== 'all' && stores && (
                <CopyToStoreModal
                    sourceId={storeSel}
                    stores={stores}
                    authHeaders={authHeaders}
                    onClose={() => setCopyOpen(false)}
                    onCopied={() => { setCopyOpen(false); refreshItems() }}
                />
            )}

            {settingsStore && (
                <StoreSettingsModal
                    store={settingsStore}
                    authHeaders={authHeaders}
                    onClose={() => { setSettingsStore(null); qc.invalidateQueries({ queryKey: ['neverout-stores'] }) }}
                />
            )}

            {editItem && (
                <ProductEditModal
                    seed={editItem}
                    storeName={storeName}
                    authHeaders={authHeaders}
                    onClose={closeEdit}
                    onSaved={() => { closeEdit(); refreshItems() }}
                />
            )}

        </>
    )
}

// ── Copy monitored SKUs to another store ──────────────────────────────

function CopyToStoreModal({ sourceId, stores, authHeaders, onClose, onCopied }: {
    sourceId: string
    stores: NoStore[]
    authHeaders: Record<string, string>
    onClose: () => void
    onCopied: () => void
}) {
    const source = stores.find(s => s.storeId === sourceId)
    const targets = stores.filter(s => s.storeId !== sourceId)
    const [targetId, setTargetId] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<string | null>(null)

    const copy = async () => {
        if (!targetId) { setError('Pick a target store.'); return }
        setBusy(true)
        setError(null)
        try {
            const res = await fetch(API_BASE + `/api/neverout/stores/${sourceId}/copy/${targetId}`, {
                method: 'POST', headers: authHeaders,
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.error ?? 'Copy failed.')
            setResult(`Copied ${json.copied} SKU${json.copied === 1 ? '' : 's'} to ${stores.find(s => s.storeId === targetId)?.name}`
                + (json.skipped > 0 ? ` (${json.skipped} already monitored there).` : '.'))
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setBusy(false)
        }
    }

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 460 }}>
                <div className="modal-header">
                    <h2>Copy SKUs to another store</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {result ? (
                        <p style={{ marginTop: 0 }}>✅ {result}</p>
                    ) : (
                        <>
                            <p style={{ marginTop: 0, fontSize: 'var(--text-sm)' }}>
                                Copies every monitored SKU (and its min/max thresholds) from{' '}
                                <strong>{source?.name ?? sourceId}</strong> to the store below.
                                SKUs the target already monitors are left untouched.
                            </p>
                            <p className="no-inline-note">
                                The target store's schedule, nag interval, and contacts are <strong>not</strong> changed —
                                configure those under ⚙️ Store settings.
                            </p>
                            <div className="form-field">
                                <label className="form-label">Copy to</label>
                                <select className="form-select" value={targetId} onChange={e => setTargetId(e.target.value)}>
                                    <option value="">Choose a store…</option>
                                    {targets.map(s => (
                                        <option key={s.storeId} value={s.storeId}>{s.name} ({s.itemCount} monitored)</option>
                                    ))}
                                </select>
                            </div>
                            {error && <div className="no-error-banner">{error}</div>}
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    {result ? (
                        <button className="btn btn-primary" onClick={onCopied}>Done</button>
                    ) : (
                        <>
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button className="btn btn-primary" onClick={copy} disabled={busy || !targetId}>
                                {busy ? 'Copying…' : 'Copy SKUs'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Product threshold editor (all variants + product total) ───────────

type NoProductVariant = {
    itemId: string | null
    colorId: number
    sizeId: number
    colorName: string | null
    sizeName: string | null
    currentQuantity: number | null
    minQty: number | null
    maxQty: number | null
    status: 'Ok' | 'BelowMin' | 'AboveMax'
    isSilenced: boolean
}

type NoProductTotal = {
    itemId: string | null
    currentQuantity: number | null
    minQty: number | null
    maxQty: number | null
    status: 'Ok' | 'BelowMin' | 'AboveMax'
    isSilenced: boolean
}

type NoProductResponse = {
    storeId: string
    itemCardId: number
    description: string
    styleCode: string | null
    prodNo: string | null
    brand: string | null
    liveError: boolean
    serverTimeZone: string | null
    serverTimeZoneName: string | null
    serverUtcOffsetMinutes: number | null
    createdAt: string | null
    createdBy: string | null
    updatedAt: string | null
    updatedBy: string | null
    productTotal: NoProductTotal
    variants: NoProductVariant[]
}

// Editable min/max as strings (blank min = not monitored).
type ThresholdDraft = { min: string; max: string }

// A stable signature of the draft threshold rows, for dirty-tracking.
const draftSignature = (rows: Record<string, ThresholdDraft>) =>
    Object.entries(rows)
        .map(([k, d]) => `${k}=${d.min.trim()}/${d.max.trim()}`)
        .sort()
        .join('|')

type SaveVariant = { colorId: number; sizeId: number; minQty: number | null; maxQty: number | null; colorName: string | null; sizeName: string | null }
type SavePayload = { variants: SaveVariant[]; total: { min: number | null; max: number | null } }

function ProductEditModal({ seed, storeName, authHeaders, onClose, onSaved }: {
    seed: NoItem
    storeName: (id: string) => string
    authHeaders: Record<string, string>
    onClose: () => void
    onSaved: () => void
}) {
    const [rows, setRows] = useState<Record<string, ThresholdDraft>>({}) // key: "c:s" or "total"
    const [mode, setMode] = useState<'min' | 'max'>('min')
    const [showInv, setShowInv] = useState(true)
    const [hideOOS, setHideOOS] = useState(false)
    const [leadDays, setLeadDays] = useState('14')
    const [suggesting, setSuggesting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [baseline, setBaseline] = useState('') // draft signature when loaded, for dirty-tracking
    const [pendingClear, setPendingClear] = useState<SavePayload | null>(null) // set when Update would stop all monitoring

    // The dialog auto-fits its width to the colour/size grid, and can be resized from a corner
    // grip. modalRef/gridScrollRef drive the auto-fit; userSizedRef stops us fighting a manual drag.
    const modalRef = useRef<HTMLDivElement>(null)
    const gridScrollRef = useRef<HTMLDivElement>(null)
    const userSizedRef = useRef(false)

    const { data, isLoading } = useQuery<NoProductResponse>({
        queryKey: ['neverout-product', seed.storeId, seed.itemCardId],
        queryFn: async () => {
            const res = await fetch(
                API_BASE + `/api/neverout/product?storeId=${seed.storeId}&itemCardId=${seed.itemCardId}`,
                { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    // Seed the editable drafts once the data arrives.
    useEffect(() => {
        if (!data) return
        const draft: Record<string, ThresholdDraft> = {
            total: {
                min: data.productTotal.minQty === null ? '' : String(data.productTotal.minQty),
                max: data.productTotal.maxQty === null ? '' : String(data.productTotal.maxQty),
            },
        }
        for (const v of data.variants) {
            draft[`${v.colorId}:${v.sizeId}`] = {
                min: v.minQty === null ? '' : String(v.minQty),
                max: v.maxQty === null ? '' : String(v.maxQty),
            }
        }
        setRows(draft)
        setBaseline(draftSignature(draft))
    }, [data])

    const dirty = draftSignature(rows) !== baseline

    const setField = (key: string, field: 'min' | 'max', value: string) =>
        setRows(r => ({ ...r, [key]: { ...(r[key] ?? { min: '', max: '' }), [field]: value } }))

    const parseLine = (d: ThresholdDraft | undefined): { min: number | null; max: number | null } | string => {
        if (!d || d.min.trim() === '') return { min: null, max: null } // blank min = not monitored
        const min = parseInt(d.min, 10)
        if (isNaN(min) || min < 0) return 'Min must be 0 or more.'
        let max: number | null = null
        if (d.max.trim() !== '') {
            max = parseInt(d.max, 10)
            if (isNaN(max) || max < min) return 'Max must be blank or at least the min.'
        }
        return { min, max }
    }

    const commit = async ({ variants, total }: SavePayload) => {
        if (!data) return
        setSaving(true)
        try {
            const res = await fetch(API_BASE + '/api/neverout/product', {
                method: 'PUT',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    storeId: data.storeId,
                    itemCardId: data.itemCardId,
                    description: data.description,
                    styleCode: data.styleCode,
                    prodNo: data.prodNo,
                    brand: data.brand,
                    productTotal: { minQty: total.min, maxQty: total.max },
                    variants,
                }),
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.error ?? 'Failed to save thresholds.')
            onSaved()
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setSaving(false)
        }
    }

    const save = () => {
        if (!data) return
        setError(null)
        const totalParsed = parseLine(rows['total'])
        if (typeof totalParsed === 'string') { setError(`Product total: ${totalParsed}`); return }

        const variants: SaveVariant[] = []
        for (const v of data.variants) {
            const parsed = parseLine(rows[`${v.colorId}:${v.sizeId}`])
            if (typeof parsed === 'string') {
                setError(`${variantLabel(v.colorName, v.sizeName) || 'Variant'}: ${parsed}`)
                return
            }
            variants.push({ colorId: v.colorId, sizeId: v.sizeId, minQty: parsed.min, maxQty: parsed.max, colorName: v.colorName, sizeName: v.sizeName })
        }

        const payload: SavePayload = { variants, total: totalParsed }

        // Clearing every line deletes all monitoring for this product — gate it behind the same
        // confirmation as removing the item from the list.
        const willMonitorNothing = totalParsed.min === null && variants.every(v => v.minQty === null)
        if (willMonitorNothing) { setPendingClear(payload); return }

        void commit(payload)
    }

    const monitoredCount = Object.entries(rows).filter(([, d]) => d.min.trim() !== '').length
    const qty = (n: number | null) => (n === null ? '—' : n)

    // Non-grid product: one placeholder colour/size variant — variant and total are the same
    // thing, so render plain labelled min/max boxes instead of a grid. Zero variants (nothing
    // in StoreInv anywhere, e.g. a total-only monitor on a card with no stock rows) also gets
    // the plain form; an empty grid is meaningless.
    const nonGrid = !!data && (data.variants.length === 0
        || (data.variants.length === 1
            && isPlaceholderName(data.variants[0].colorName) && isPlaceholderName(data.variants[0].sizeName)))

    // Colour rows × size columns, same layout as the add dialog's picker.
    const grid = useMemo(() => {
        if (!data) return null
        const colours = new Map<number, string>()
        const sizes = new Map<number, string>()
        const cells = new Map<string, NoProductVariant>()
        for (const v of data.variants) {
            colours.set(v.colorId, v.colorName ?? '—')
            sizes.set(v.sizeId, v.sizeName ?? '—')
            cells.set(`${v.colorId}:${v.sizeId}`, v)
        }
        return {
            colours: [...colours.entries()].sort((a, b) => a[1].localeCompare(b[1])),
            sizes: [...sizes.entries()].sort((a, b) => sizeCompare(a[1], b[1])),
            cells,
        }
    }, [data])

    // Catalog monsters (hundreds of stale colours) default to hiding out-of-stock rows.
    useEffect(() => {
        if (grid) setHideOOS(!data?.liveError && grid.colours.length > 25)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    // Auto-fit the dialog width to the grid's natural width (so a wide colour/size matrix shows
    // without an inner horizontal scrollbar), capped at the viewport. A CSS max-width also caps it,
    // and once the user drags the corner grip we stop adjusting so we don't override their size.
    useEffect(() => {
        if (userSizedRef.current) return
        const modal = modalRef.current
        const wrap = gridScrollRef.current
        const table = wrap?.querySelector('table')
        if (!modal || !wrap || !table) return
        // Everything around the grid content horizontally (body padding, borders, scrollbar).
        const chrome = modal.offsetWidth - wrap.clientWidth
        const desired = Math.min(table.scrollWidth + chrome + 2, Math.floor(window.innerWidth * 0.96))
        modal.style.width = `${desired}px`
    }, [data, showInv, hideOOS])

    // A colour row survives the OOS filter if any cell has stock, a draft threshold, or an
    // unknown quantity (legacy unreachable) — never hide something the user is working with.
    const rowVisible = (colourId: number) => {
        if (!hideOOS || !grid || !data) return true
        for (const [sizeId] of grid.sizes) {
            const v = grid.cells.get(`${colourId}:${sizeId}`)
            if (!v) continue
            if (v.currentQuantity === null || v.currentQuantity > 0) return true
            const d = rows[`${v.colorId}:${v.sizeId}`]
            if (d && (d.min.trim() !== '' || d.max.trim() !== '')) return true
        }
        return false
    }

    // Cell tint from the line's persisted status (only meaningful while it stays monitored).
    const cellStatusClass = (status: 'Ok' | 'BelowMin' | 'AboveMax', draft: ThresholdDraft | undefined) => {
        if (!draft || draft.min.trim() === '') return undefined
        if (status === 'BelowMin') return 'no-grid-below'
        if (status === 'AboveMax') return 'no-grid-above'
        return 'no-grid-active'
    }

    // Pre-fill mins from 60-day sales velocity (min mode only).
    const suggestMins = async () => {
        if (!data) return
        const lead = parseInt(leadDays, 10)
        if (isNaN(lead) || lead < 1 || lead > 365) { setError('Lead time must be 1–365 days.'); return }
        setSuggesting(true)
        setError(null)
        try {
            const res = await fetch(
                API_BASE + `/api/neverout/velocity?itemCardId=${data.itemCardId}&storeIds=${data.storeId}`,
                { headers: authHeaders })
            if (!res.ok) throw new Error('Sales data is unavailable right now.')
            const vel: { windowDays: number; rows: Array<{ colorId: number; sizeId: number; units: number }> } = await res.json()
            const byKey = new Map(vel.rows.map(r => [`${r.colorId}:${r.sizeId}`, r.units]))
            // Compute synchronously so the count is accurate when checked (see note in the add
            // dialog's suggestMins) — a state updater may run after this function returns.
            const next = { ...rows }
            let filled = 0
            for (const v of data.variants) {
                const key = `${v.colorId}:${v.sizeId}`
                const units = byKey.get(key) ?? 0
                if (units <= 0) continue
                next[key] = { ...(next[key] ?? { min: '', max: '' }), min: String(Math.max(1, Math.ceil((units / vel.windowDays) * lead))) }
                filled++
            }
            setRows(next)
            if (filled === 0) setError('No sales in the last 60 days for these variants at this store — nothing to suggest.')
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setSuggesting(false)
        }
    }

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <>
        <div className="modal-backdrop">
            <div
                className="modal no-product-modal"
                ref={modalRef}
                onPointerDown={e => {
                    // Grabbing the bottom-right resize grip: stop auto-fitting from here on.
                    const r = modalRef.current?.getBoundingClientRect()
                    if (r && e.clientX > r.right - 22 && e.clientY > r.bottom - 22) userSizedRef.current = true
                }}
            >
                <div className="modal-header">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        {seed.description || `Item ${seed.itemCardId}`}
                        {data && (data.createdAt || data.updatedAt) && (
                            <span className="no-audit-info" tabIndex={0}
                                  title={[
                                      data.createdAt ? `Created ${fmtWhen(data.createdAt)}${data.createdBy ? ` by ${data.createdBy}` : ''}` : null,
                                      data.updatedAt && data.updatedAt !== data.createdAt
                                          ? `Last updated ${fmtWhen(data.updatedAt)}${data.updatedBy ? ` by ${data.updatedBy}` : ''}` : null,
                                  ].filter(Boolean).join('\n')}>ⓘ</span>
                        )}
                    </h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <p className="no-inline-note" style={{ marginTop: 0 }}>
                        {storeName(seed.storeId)} · {[seed.styleCode, seed.prodNo && seed.prodNo !== seed.styleCode ? seed.prodNo : null, seed.brand].filter(Boolean).join(' · ')}
                    </p>
                    {data && data.updatedAt && data.updatedAt !== data.createdAt && (
                        <p className="no-inline-note" style={{ margin: '0 0 var(--space-2)' }}>
                            Last updated {fmtWhen(data.updatedAt)}{data.updatedBy ? ` by ${data.updatedBy}` : ''}
                        </p>
                    )}
                    {isLoading || !data ? (
                        <div className="no-inline-note">Loading variants…</div>
                    ) : nonGrid ? (
                        // Single-variant product: plain labelled boxes, no grid chrome. The
                        // min/max edit whichever line is actually monitored (total-only rows
                        // came from the add dialog's product-total option).
                        (() => {
                            const v = data.variants[0] as NoProductVariant | undefined
                            const vKey = v ? `${v.colorId}:${v.sizeId}` : null
                            const variantMonitored = v?.itemId != null
                            const totalMonitored = data.productTotal.itemId !== null
                            const lines: Array<{ key: string; label: string | null; status: 'Ok' | 'BelowMin' | 'AboveMax'; silenced: boolean }> =
                                totalMonitored && variantMonitored && vKey
                                    ? [
                                        { key: vKey, label: 'This item', status: v!.status, silenced: v!.isSilenced },
                                        { key: 'total', label: 'Product total', status: data.productTotal.status, silenced: data.productTotal.isSilenced },
                                    ]
                                    : !totalMonitored && vKey
                                        ? [{ key: vKey, label: null, status: v!.status, silenced: v!.isSilenced }]
                                        : [{ key: 'total', label: null, status: data.productTotal.status, silenced: data.productTotal.isSilenced }]
                            return (
                                <>
                                    <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)' }}>
                                        On hand: <strong>{qty(v ? v.currentQuantity : data.productTotal.currentQuantity)}</strong>
                                        {data.liveError && <span className="no-inline-note"> (live quantity unavailable)</span>}
                                    </p>
                                    {lines.map(line => (
                                        <div key={line.key} style={{ marginBottom: 'var(--space-2)' }}>
                                            {line.label && <div className="form-label" style={{ marginBottom: 'var(--space-1)' }}>{line.label}</div>}
                                            <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end' }}>
                                                <div className="form-field" style={{ marginBottom: 0 }}>
                                                    <label className="form-label">Min (never below)</label>
                                                    <StepInput value={rows[line.key]?.min ?? ''} placeholder="—" width={110}
                                                        onChange={val => setField(line.key, 'min', val)} />
                                                </div>
                                                <div className="form-field" style={{ marginBottom: 0 }}>
                                                    <label className="form-label">Max (blank = none)</label>
                                                    <StepInput value={rows[line.key]?.max ?? ''} placeholder="—" width={110}
                                                        onChange={val => setField(line.key, 'max', val)} />
                                                </div>
                                                <div style={{ paddingBottom: 6 }}>
                                                    {thresholdStatusPill(line.status, rows[line.key], line.silenced)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <p className="no-inline-note" style={{ margin: 'var(--space-2) 0 0' }}>
                                        Clear the Min and Max to stop monitoring this item.
                                    </p>
                                    {error && <div className="no-error-banner" style={{ marginTop: 'var(--space-3)' }}>{error}</div>}
                                </>
                            )
                        })()
                    ) : (
                        <>
                            <p className="no-inline-note" style={{ margin: '0 0 var(--space-3)' }}>
                                Set a <strong>Min</strong> in a cell to monitor that line; clear the Min to stop monitoring it.
                                Flip to <strong>Max</strong> to edit overstock thresholds. Arrow keys move between cells; Enter saves.
                            </p>
                            {data.liveError && <div className="no-error-banner" style={{ marginBottom: 'var(--space-3)' }}>Live quantities are currently unavailable — thresholds still save.</div>}

                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
                                <div className="no-mode-toggle">
                                    <button className={mode === 'min' ? 'active' : ''} onClick={() => setMode('min')}>Min</button>
                                    <button className={mode === 'max' ? 'active' : ''} onClick={() => setMode('max')}>Max</button>
                                </div>
                                <span style={{ flex: 1 }} />
                                {mode === 'min' && (
                                    <>
                                        <button className="btn btn-secondary" disabled={suggesting} onClick={suggestMins}
                                                title="Pre-fill mins from the last 60 days of sales at this store">
                                            {suggesting ? 'Suggesting…' : '✨ Suggest mins'}
                                        </button>
                                        <label className="no-inline-note" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            lead time
                                            <input className="no-grid-input" style={{ width: 44 }} type="number" min={1} max={365}
                                                   value={leadDays} onChange={e => setLeadDays(e.target.value)} />
                                            days
                                        </label>
                                    </>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={showInv} onChange={e => setShowInv(e.target.checked)} />
                                    Show inventory
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={hideOOS} onChange={e => setHideOOS(e.target.checked)} />
                                    Hide out of stock
                                    {hideOOS && grid && (() => {
                                        const hidden = grid.colours.length - grid.colours.filter(([id]) => rowVisible(id)).length
                                        return hidden > 0 ? <span className="no-inline-note">({hidden} hidden)</span> : null
                                    })()}
                                </label>
                            </div>

                            {/* Product total lives outside the colour/size grid: one line across
                                all variants, with its own min & max regardless of the grid's mode. */}
                            <div className="no-total-block" title="One monitor line on the summed on-hand across every colour/size">
                                <span className="no-total-label">
                                    <strong>Product total</strong> <span className="no-inline-note">(all colours/sizes)</span>
                                    {showInv && <span className="no-grid-inv"> {qty(data.productTotal.currentQuantity)} on hand</span>}
                                </span>
                                <label className="no-inline-note">Min
                                    <StepInput value={rows['total']?.min ?? ''} placeholder="—"
                                        onChange={v => setField('total', 'min', v)} />
                                </label>
                                <label className="no-inline-note">Max
                                    <StepInput value={rows['total']?.max ?? ''} placeholder="—"
                                        onChange={v => setField('total', 'max', v)} />
                                </label>
                                {thresholdStatusPill(data.productTotal.status, rows['total'], data.productTotal.isSilenced)}
                            </div>

                            {grid && (
                                <div className="no-grid-scroll" ref={gridScrollRef}>
                                    <table className="no-size-grid" onKeyDown={e => gridKeyNav(e, save)}>
                                        <thead>
                                            <tr>
                                                <th className="no-grid-colour">Colour</th>
                                                {grid.sizes.map(([id, label]) => <th key={id}>{isPlaceholderName(label) ? '—' : label}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {grid.colours.filter(([id]) => rowVisible(id)).map(([colourId, colourLabel], rowIdx) => (
                                                <tr key={colourId}>
                                                    <td className="no-grid-colour">{colourLabel}</td>
                                                    {grid.sizes.map(([sizeId], colIdx) => {
                                                        const v = grid.cells.get(`${colourId}:${sizeId}`)
                                                        if (!v) return <td key={sizeId} className="no-grid-void" />
                                                        const key = `${v.colorId}:${v.sizeId}`
                                                        return (
                                                            <td key={sizeId} className={cellStatusClass(v.status, rows[key])}
                                                                title={v.isSilenced ? 'Silenced' : undefined}>
                                                                {showInv && <span className="no-grid-inv">{qty(v.currentQuantity)}</span>}
                                                                <StepInput value={rows[key]?.[mode] ?? ''}
                                                                    dataR={rowIdx + 1} dataC={colIdx + 1}
                                                                    onChange={val => setField(key, mode, val)} />
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {error && <div className="no-error-banner" style={{ marginTop: 'var(--space-3)' }}>{error}</div>}
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <span className="no-inline-note" style={{ flex: 1 }}>{monitoredCount} line{monitoredCount === 1 ? '' : 's'} monitored</span>
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={save} disabled={saving || !data || !dirty}
                            title={!dirty ? 'No changes to save' : undefined}>{saving ? 'Saving…' : 'Update'}</button>
                </div>
            </div>
        </div>
        {pendingClear && (
            <ConfirmDialog
                title="Stop monitoring?"
                message={`This clears every threshold and stops monitoring "${data?.description ?? seed.description}" at this store. Continue?`}
                confirmLabel="Stop monitoring"
                danger
                onCancel={() => setPendingClear(null)}
                onConfirm={() => { const p = pendingClear; setPendingClear(null); void commit(p) }}
            />
        )}
        </>
    )
}

// Pill for a threshold row: shows the persisted breach status, but greys out if the line's
// Min has been cleared in the draft (it will stop being monitored on save).
function thresholdStatusPill(status: 'Ok' | 'BelowMin' | 'AboveMax', draft: ThresholdDraft | undefined, silenced: boolean) {
    if (!draft || draft.min.trim() === '') return <span className="no-pill no-pill-unchecked">Off</span>
    if (status === 'BelowMin') return <span className={`no-pill ${silenced ? 'no-pill-silenced' : 'no-pill-below'}`}>{silenced ? '🔕 Below' : 'Below min'}</span>
    if (status === 'AboveMax') return <span className={`no-pill ${silenced ? 'no-pill-silenced' : 'no-pill-above'}`}>{silenced ? '🔕 Over' : 'Overstock'}</span>
    return <span className="no-pill no-pill-ok">OK</span>
}

// ── Add SKUs modal ────────────────────────────────────────────────────

function AddSkusModal({ storeIds, storeName, authHeaders, onClose, onAdded }: {
    storeIds: string[]
    storeName: (id: string) => string
    authHeaders: Record<string, string>
    onClose: () => void
    onAdded: () => void
}) {
    const [query, setQuery] = useState('')
    const [debounced, setDebounced] = useState('')
    const [style, setStyle] = useState<NoSearchResult | null>(null)
    // Per-variant Min entries keyed "colorId:sizeId" — a filled cell means "monitor this
    // variant with that min". The product total has its own entry.
    const [cellMins, setCellMins] = useState<Record<string, string>>({})
    const [totalMin, setTotalMin] = useState('')
    const [max, setMax] = useState('')
    const [showInv, setShowInv] = useState(true)
    const [hideOOS, setHideOOS] = useState(false)
    const [leadDays, setLeadDays] = useState('14')
    const [suggesting, setSuggesting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    const resetPicks = () => { setCellMins({}); setTotalMin(''); setError(null) }

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 300)
        return () => clearTimeout(t)
    }, [query])

    const { data: results, isFetching: searching } = useQuery<NoSearchResult[]>({
        queryKey: ['neverout-search', debounced, storeIds.join(',')],
        enabled: debounced.length >= 2 && !style,
        queryFn: async () => {
            const res = await fetch(
                API_BASE + `/api/neverout/search?q=${encodeURIComponent(debounced)}&storeIds=${storeIds.join(',')}`,
                { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    const { data: variantsData, isFetching: variantsLoading } = useQuery<{ productTotalMonitoredIn: string[]; variants: NoVariant[] }>({
        queryKey: ['neverout-variants', style?.itemCardId, storeIds.join(',')],
        enabled: !!style,
        queryFn: async () => {
            const res = await fetch(
                API_BASE + `/api/neverout/variants?itemCardId=${style!.itemCardId}&storeIds=${storeIds.join(',')}`,
                { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })
    const variants = variantsData?.variants

    const variantKey = (v: NoVariant) => `${v.colorId}:${v.sizeId}`
    const isFullyMonitored = (v: NoVariant) => v.monitoredIn.length === storeIds.length

    // Non-grid products (single variant with placeholder colour/size like "Not Applicable / -")
    // get no size grid and no product-total option — there's only one thing to monitor.
    const isSingleVariant = !!variants && variants.length === 1
        && isPlaceholderName(variants[0].colorName) && isPlaceholderName(variants[0].sizeName)

    // Default the lone variant of a non-grid product to min 1.
    useEffect(() => {
        if (isSingleVariant && !isFullyMonitored(variants![0]))
            setCellMins({ [variantKey(variants![0])]: '1' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSingleVariant, variantsData])

    // Product-total: monitors the summed on-hand across every colour/size.
    const totalMonitoredEverywhere =
        (variantsData?.productTotalMonitoredIn?.length ?? 0) >= storeIds.length
    const invOf = (v: NoVariant) =>
        Object.values(v.storeQuantities).reduce((a, b) => a + b, 0)
    const totalInv = variants?.reduce((sum, v) => sum + invOf(v), 0) ?? 0
    const invTitle = (v: NoVariant) =>
        storeIds.map(s => `${storeName(s)}: ${v.storeQuantities[s.toLowerCase()] ?? 0}`).join('\n')

    // Colour rows × size columns for the grid (like the Puresoft4 colour/size picker).
    const grid = useMemo(() => {
        if (!variants) return null
        const colours = new Map<number, string>()
        const sizes = new Map<number, string>()
        const cells = new Map<string, NoVariant>()
        for (const v of variants) {
            colours.set(v.colorId, v.colorName ?? '—')
            sizes.set(v.sizeId, v.sizeName ?? '—')
            cells.set(variantKey(v), v)
        }
        return {
            colours: [...colours.entries()].sort((a, b) => a[1].localeCompare(b[1])),
            sizes: [...sizes.entries()].sort((a, b) => sizeCompare(a[1], b[1])),
            cells,
        }
    }, [variants])

    const setCell = (key: string, value: string) =>
        setCellMins(m => ({ ...m, [key]: value }))

    // Catalog monsters (hundreds of stale colours) default to hiding out-of-stock rows.
    useEffect(() => {
        if (grid) setHideOOS(grid.colours.length > 25)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variantsData])

    // A colour row survives the OOS filter if any of its cells has stock, a typed min,
    // or is already monitored — never hide something the user is working with.
    const rowVisible = (colourId: number) => {
        if (!hideOOS || !grid) return true
        for (const [sizeId] of grid.sizes) {
            const v = grid.cells.get(`${colourId}:${sizeId}`)
            if (!v) continue
            if (invOf(v) > 0) return true
            if ((cellMins[variantKey(v)] ?? '').trim() !== '') return true
            if (v.monitoredIn.length > 0) return true
        }
        return false
    }

    const fillAll = (value: string) => {
        if (!variants || !grid) return
        const next: Record<string, string> = {}
        if (value !== '')
            for (const [colourId] of grid.colours.filter(([id]) => rowVisible(id)))
                for (const [sizeId] of grid.sizes) {
                    const v = grid.cells.get(`${colourId}:${sizeId}`)
                    if (v && !isFullyMonitored(v)) next[variantKey(v)] = value
                }
        setCellMins(next)
    }

    // Pre-fill mins from 60-day sales velocity: min ≈ daily rate × lead time, at least 1.
    // Cells for variants with no sales are left untouched.
    const suggestMins = async () => {
        if (!style || !variants) return
        const lead = parseInt(leadDays, 10)
        if (isNaN(lead) || lead < 1 || lead > 365) { setError('Lead time must be 1–365 days.'); return }
        setSuggesting(true)
        setError(null)
        try {
            const res = await fetch(
                API_BASE + `/api/neverout/velocity?itemCardId=${style.itemCardId}&storeIds=${storeIds.join(',')}`,
                { headers: authHeaders })
            if (!res.ok) throw new Error('Sales data is unavailable right now.')
            const data: { windowDays: number; rows: Array<{ colorId: number; sizeId: number; units: number }> } = await res.json()
            const byKey = new Map(data.rows.map(r => [`${r.colorId}:${r.sizeId}`, r.units]))
            // Compute the fills synchronously so the count is accurate when we check it — a
            // React state updater may run after this function returns, so counting inside it
            // and reading the count immediately would race.
            const next = { ...cellMins }
            let filled = 0
            for (const v of variants) {
                if (isFullyMonitored(v)) continue
                const units = byKey.get(variantKey(v)) ?? 0
                if (units <= 0) continue
                next[variantKey(v)] = String(Math.max(1, Math.ceil((units / data.windowDays) * lead)))
                filled++
            }
            setCellMins(next)
            if (filled === 0) setError('No sales in the last 60 days for these variants at the selected store(s) — nothing to suggest.')
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setSuggesting(false)
        }
    }

    const enteredCount = Object.values(cellMins).filter(v => v.trim() !== '').length
        + (totalMin.trim() !== '' ? 1 : 0)

    const add = async () => {
        if (!style || !variants) return
        setError(null)

        let maxVal: number | null = null
        if (max.trim() !== '') {
            maxVal = parseInt(max, 10)
            if (isNaN(maxVal) || maxVal < 0) { setError('Max must be blank or 0 or more.'); return }
        }

        type AddLine = {
            itemCardId: number; colorId: number; sizeId: number; minQty: number; maxQty: number | null
            description: string; styleCode: string | null; prodNo: string | null; brand: string | null
            colorName: string | null; sizeName: string | null; scope?: 'ProductTotal'
        }
        const items: AddLine[] = []
        for (const v of variants) {
            const raw = cellMins[variantKey(v)]?.trim()
            if (!raw) continue
            const minVal = parseInt(raw, 10)
            if (isNaN(minVal) || minVal < 0) {
                setError(`${variantLabel(v.colorName, v.sizeName) || 'Variant'}: min must be 0 or more.`)
                return
            }
            if (maxVal !== null && maxVal < minVal) {
                setError(`${variantLabel(v.colorName, v.sizeName) || 'Variant'}: max (${maxVal}) is below its min (${minVal}).`)
                return
            }
            items.push({
                itemCardId: style.itemCardId,
                colorId: v.colorId,
                sizeId: v.sizeId,
                minQty: minVal,
                maxQty: maxVal,
                description: style.description,
                styleCode: style.styleCode,
                prodNo: style.prodNo,
                brand: style.brand,
                colorName: v.colorName,
                sizeName: v.sizeName,
            })
        }
        if (totalMin.trim() !== '') {
            const t = parseInt(totalMin, 10)
            if (isNaN(t) || t < 0) { setError('Product total: min must be 0 or more.'); return }
            if (maxVal !== null && maxVal < t) { setError(`Product total: max (${maxVal}) is below its min (${t}).`); return }
            items.push({
                itemCardId: style.itemCardId,
                colorId: 0,
                sizeId: 0,
                minQty: t,
                maxQty: maxVal,
                description: style.description,
                styleCode: style.styleCode,
                prodNo: style.prodNo,
                brand: style.brand,
                colorName: null,
                sizeName: null,
                scope: 'ProductTotal',
            })
        }
        if (items.length === 0) {
            setError('Enter a Min in at least one cell (or on the product total).')
            return
        }

        setSaving(true)
        try {
            const res = await fetch(API_BASE + '/api/neverout/items', {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ storeIds, items }),
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.error ?? 'Failed to add SKUs.')
            onAdded()
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setSaving(false)
        }
    }

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 820 }}>
                <div className="modal-header">
                    <h2>Add SKUs to monitoring</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <p className="no-inline-note" style={{ marginTop: 0 }}>
                        Adding to: {storeIds.map(storeName).join(', ')}
                    </p>

                    {!style ? (
                        <>
                            <div className="form-field">
                                <label className="form-label">Search products (name, style code, supplier code)</label>
                                <input
                                    className="form-input"
                                    autoFocus
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="e.g. crew sock, B-1234…"
                                />
                            </div>
                            {searching && <div className="no-inline-note">Searching…</div>}
                            {results && results.length === 0 && debounced.length >= 2 && (
                                <div className="no-inline-note">No products with inventory match "{debounced}".</div>
                            )}
                            {results && results.length > 0 && (
                                <div className="no-search-results">
                                    {results.map(r => (
                                        <div key={r.itemCardId} className="no-search-result" onClick={() => { setStyle(r); resetPicks() }}>
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{r.description}</div>
                                                <div className="no-inline-note">
                                                    {[r.styleCode, r.prodNo && r.prodNo !== r.styleCode ? r.prodNo : null, r.brand]
                                                        .filter(Boolean).join(' · ')}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', whiteSpace: 'nowrap' }}>
                                                <span className="no-qty-chip"
                                                      title={storeIds.length > 1 ? 'Total on hand across the selected stores' : undefined}>
                                                    {r.storeQuantity} on hand
                                                </span>
                                                <span className="btn btn-secondary" style={{ pointerEvents: 'none' }}>Pick sizes →</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                                <div>
                                    <div style={{ fontWeight: 700 }}>{style.description}</div>
                                    <div className="no-inline-note">{[style.styleCode, style.brand].filter(Boolean).join(' · ')}</div>
                                </div>
                                <button className="btn btn-secondary" onClick={() => { setStyle(null); resetPicks() }}>← Back to search</button>
                            </div>

                            {variantsLoading ? (
                                <div className="no-inline-note">Loading variants…</div>
                            ) : !variants || variants.length === 0 ? (
                                <div className="no-inline-note">No inventory variants found for this product.</div>
                            ) : isSingleVariant ? (
                                // Non-grid product ("-"/"Not Applicable" colour & size): nothing to
                                // pick — one monitor line, just set the thresholds.
                                <>
                                    <div className="no-variant-row selected" style={{ cursor: 'default' }}>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontWeight: 600 }}>Single-variant product</span>
                                            {isFullyMonitored(variants[0])
                                                ? <span className="no-inline-note"> — already monitored at every selected store</span>
                                                : <div className="no-inline-note">One monitor line will be added.</div>}
                                        </div>
                                        <div>
                                            {storeIds.map(s => (
                                                <span key={s} className="no-qty-chip" title={storeName(s)}>
                                                    {storeName(s).slice(0, 12)}: {variants[0].storeQuantities[s.toLowerCase()] ?? 0}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
                                        <div className="form-field">
                                            <label className="form-label">Min (never below)</label>
                                            <StepInput value={cellMins[variantKey(variants[0])] ?? ''} width={110}
                                                onChange={val => setCell(variantKey(variants[0]), val)} />
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Max (blank = none)</label>
                                            <StepInput value={max} placeholder="—" width={110} onChange={setMax} />
                                        </div>
                                    </div>
                                </>
                            ) : grid && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
                                        <span className="no-inline-note" style={{ flex: 1, minWidth: 180 }}>
                                            Type a <strong>Min</strong> in a cell to monitor that colour/size. Arrow keys move between cells; Enter adds.
                                        </span>
                                        <button className="btn btn-secondary" disabled={suggesting} onClick={suggestMins}
                                                title="Pre-fill mins from the last 60 days of sales at the selected store(s)">
                                            {suggesting ? 'Suggesting…' : '✨ Suggest mins'}
                                        </button>
                                        <label className="no-inline-note" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            lead time
                                            <input className="no-grid-input" style={{ width: 44 }} type="number" min={1} max={365}
                                                   value={leadDays} onChange={e => setLeadDays(e.target.value)} />
                                            days
                                        </label>
                                        <button className="btn btn-secondary" onClick={() => fillAll('1')}>Min 1 on all</button>
                                        <button className="btn btn-secondary" onClick={() => fillAll('')}>Clear</button>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={showInv} onChange={e => setShowInv(e.target.checked)} />
                                            Show inventory
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={hideOOS} onChange={e => setHideOOS(e.target.checked)} />
                                            Hide out of stock
                                            {hideOOS && grid && (() => {
                                                const hidden = grid.colours.length - grid.colours.filter(([id]) => rowVisible(id)).length
                                                return hidden > 0 ? <span className="no-inline-note">({hidden} hidden)</span> : null
                                            })()}
                                        </label>
                                    </div>

                                    {/* Product total lives outside the colour/size grid. */}
                                    <div className="no-total-block" title="One monitor line on the summed on-hand across every colour/size">
                                        <span className="no-total-label">
                                            <strong>Product total</strong> <span className="no-inline-note">(all colours/sizes)</span>
                                            {showInv && <span className="no-grid-inv"> {totalInv} on hand</span>}
                                        </span>
                                        {totalMonitoredEverywhere
                                            ? <span className="no-inline-note">already monitored</span>
                                            : <label className="no-inline-note">Min
                                                <StepInput value={totalMin} placeholder="—" onChange={setTotalMin} />
                                            </label>}
                                    </div>

                                    {grid.colours.filter(([id]) => rowVisible(id)).length === 0 ? (
                                        <p className="no-inline-note" style={{ margin: 'var(--space-2) 0' }}>
                                            Every colour/size is out of stock and hidden. Set a <strong>product total</strong> min above,
                                            or uncheck <strong>Hide out of stock</strong> to set per-variant thresholds.
                                        </p>
                                    ) : (
                                    <div className="no-grid-scroll">
                                        <table className="no-size-grid" onKeyDown={e => gridKeyNav(e, add)}>
                                            <thead>
                                                <tr>
                                                    <th className="no-grid-colour">Colour</th>
                                                    {grid.sizes.map(([id, label]) => <th key={id}>{isPlaceholderName(label) ? '—' : label}</th>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {grid.colours.filter(([id]) => rowVisible(id)).map(([colourId, colourLabel], rowIdx) => (
                                                    <tr key={colourId}>
                                                        <td className="no-grid-colour">{colourLabel}</td>
                                                        {grid.sizes.map(([sizeId], colIdx) => {
                                                            const v = grid.cells.get(`${colourId}:${sizeId}`)
                                                            if (!v) return <td key={sizeId} className="no-grid-void" />
                                                            const key = variantKey(v)
                                                            const monitored = isFullyMonitored(v)
                                                            return (
                                                                <td key={sizeId} className={monitored ? 'no-grid-monitored' : undefined}
                                                                    title={invTitle(v)}>
                                                                    {showInv && <span className="no-grid-inv">{invOf(v)}</span>}
                                                                    {monitored
                                                                        ? <span title="Already monitored">✓</span>
                                                                        : <StepInput value={cellMins[key] ?? ''}
                                                                            dataR={rowIdx + 1} dataC={colIdx + 1}
                                                                            onChange={val => setCell(key, val)} />}
                                                                </td>
                                                            )
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    )}

                                    <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-4)', alignItems: 'flex-end' }}>
                                        <div className="form-field" style={{ marginBottom: 0 }}>
                                            <label className="form-label">Max (blank = none)</label>
                                            <StepInput value={max} placeholder="—" width={110} onChange={setMax} />
                                        </div>
                                        <p className="no-inline-note" style={{ margin: 0, flex: 1 }}>
                                            Max applies to every entered line — fine-tune per-SKU thresholds afterwards with ✏️.
                                        </p>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {error && <div className="no-error-banner" style={{ marginTop: 'var(--space-3)' }}>{error}</div>}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    {style && (
                        <button className="btn btn-primary" onClick={add} disabled={saving || enteredCount === 0}>
                            {saving ? 'Adding…' : `Add ${enteredCount} line${enteredCount === 1 ? '' : 's'}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Store settings modal ──────────────────────────────────────────────

function StoreSettingsModal({ store, authHeaders, onClose }: {
    store: NoStore
    authHeaders: Record<string, string>
    onClose: () => void
}) {
    const qc = useQueryClient()
    const [form, setForm] = useState<NoSettings | null>(null)
    const [contacts, setContacts] = useState<NoContact[]>([])
    const [newEmail, setNewEmail] = useState('')
    const [fullLogOpen, setFullLogOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)

    const { data, isLoading } = useQuery<NoSettingsResponse>({
        queryKey: ['neverout-settings', store.storeId],
        queryFn: async () => {
            const res = await fetch(API_BASE + `/api/neverout/stores/${store.storeId}/settings`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    const { data: log } = useQuery<NoLogEntry[]>({
        queryKey: ['neverout-log', store.storeId],
        queryFn: async () => {
            const res = await fetch(API_BASE + `/api/neverout/stores/${store.storeId}/log?take=25`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })

    useEffect(() => {
        if (data) {
            setForm(data.settings)
            setContacts(data.contacts)
        }
    }, [data])

    const set = <K extends keyof NoSettings>(key: K, value: NoSettings[K]) =>
        setForm(f => (f ? { ...f, [key]: value } : f))

    const addContactEmail = () => {
        const email = newEmail.trim()
        if (!email.includes('@')) { setError('Enter a valid email address.'); return }
        if (contacts.some(c => c.email.toLowerCase() === email.toLowerCase())) { setNewEmail(''); return }
        setContacts(prev => [...prev, { email, userId: null, displayName: null }])
        setNewEmail('')
        setError(null)
    }

    const save = useMutation({
        mutationFn: async () => {
            if (!form) return
            const res = await fetch(API_BASE + `/api/neverout/stores/${store.storeId}/settings`, {
                method: 'PUT',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    enabled: form.enabled,
                    scheduleKind: form.scheduleKind,
                    dailyTime: form.dailyTime,
                    weeklyDay: form.weeklyDay,
                    intervalHours: form.intervalHours,
                    renotifyHours: form.renotifyHours,
                    contacts,
                }),
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.error ?? 'Failed to save settings.')
            return json
        },
        onSuccess: (json: { nextRunAt?: string } | undefined) => {
            setError(null)
            setNotice(`Saved.${json?.nextRunAt ? ` Next check: ${fmtWhen(json.nextRunAt)}.` : ''}`)
            qc.invalidateQueries({ queryKey: ['neverout-settings', store.storeId] })
        },
        onError: (e: Error) => { setNotice(null); setError(e.message) },
    })

    const action = useMutation({
        mutationFn: async (kind: 'test' | 'run') => {
            const res = await fetch(API_BASE + `/api/neverout/stores/${store.storeId}/${kind}`, {
                method: 'POST', headers: authHeaders,
            })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json.error ?? json.detail ?? 'Request failed.')
            return json as { message?: string }
        },
        onSuccess: json => {
            setError(null)
            setNotice(json?.message ?? 'Done.')
            qc.invalidateQueries({ queryKey: ['neverout-log', store.storeId] })
        },
        onError: (e: Error) => { setNotice(null); setError(e.message) },
    })

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 560 }}>
                <div className="modal-header">
                    <h2>Never Out settings — {store.name}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {isLoading || !form ? (
                        <div className="no-inline-note">Loading…</div>
                    ) : (
                        <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 600, cursor: 'pointer', marginBottom: 'var(--space-4)' }}>
                                <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
                                Notifications enabled for this store
                            </label>

                            <div className="form-field">
                                <label className="form-label">Check schedule</label>
                                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <select className="form-select" style={{ width: 'auto' }} value={form.scheduleKind}
                                            onChange={e => set('scheduleKind', e.target.value as NoSettings['scheduleKind'])}>
                                        <option value="Daily">Daily at</option>
                                        <option value="Weekly">Weekly on</option>
                                        <option value="EveryHours">Every</option>
                                    </select>
                                    {form.scheduleKind === 'Weekly' && (
                                        <select className="form-select" style={{ width: 'auto' }} value={form.weeklyDay}
                                                onChange={e => set('weeklyDay', parseInt(e.target.value, 10))}>
                                            {WEEKDAYS.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
                                        </select>
                                    )}
                                    {form.scheduleKind !== 'EveryHours' ? (
                                        <input className="form-input" style={{ width: 150 }} type="time" step={60} value={form.dailyTime}
                                               onChange={e => set('dailyTime', e.target.value)} />
                                    ) : (
                                        <>
                                            <input className="form-input" style={{ width: 90 }} type="number" min={1} max={168}
                                                   value={form.intervalHours}
                                                   onChange={e => set('intervalHours', parseInt(e.target.value, 10) || 1)} />
                                            <span style={{ fontSize: 'var(--text-sm)' }}>hours</span>
                                        </>
                                    )}
                                </div>
                                <p className="no-inline-note" style={{ margin: 'var(--space-1) 0 0' }}>
                                    Times are in {serverTzLabel(data?.serverTimeZoneName ?? null, data?.serverTimeZone ?? null, data?.serverUtcOffsetMinutes)}.
                                </p>
                            </div>

                            <div className="form-field">
                                <label className="form-label">While a SKU stays in alarm, re-notify every</label>
                                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                                    <input className="form-input" style={{ width: 90 }} type="number" min={1} max={720}
                                           value={form.renotifyHours}
                                           onChange={e => set('renotifyHours', parseInt(e.target.value, 10) || 1)} />
                                    <span style={{ fontSize: 'var(--text-sm)' }}>hours (until it recovers or is silenced)</span>
                                </div>
                            </div>

                            <div className="form-field">
                                <label className="form-label">Notification contacts</label>
                                {contacts.length === 0 && (
                                    <p className="no-inline-note" style={{ margin: '0 0 var(--space-2)' }}>
                                        No contacts yet — alarms can't be delivered until you add one.
                                    </p>
                                )}
                                {contacts.map(c => (
                                    <div key={c.email} className="no-contact-row">
                                        <span>{c.email}</span>
                                        <button className="btn-icon" title="Remove contact"
                                                onClick={() => setContacts(prev => prev.filter(x => x.email !== c.email))}>🗑️</button>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                    <input className="form-input" style={{ flex: 1, minWidth: 220 }} type="email" placeholder="Add an email address"
                                           value={newEmail}
                                           onChange={e => setNewEmail(e.target.value)}
                                           onKeyDown={e => { if (e.key === 'Enter') addContactEmail() }} />
                                    <button className="btn btn-secondary" onClick={addContactEmail}>Add</button>
                                </div>
                            </div>

                            <div className="no-inline-note" style={{ marginBottom: 'var(--space-3)' }}>
                                Last check: {fmtWhen(form.lastRunAt)} · Next check: {fmtWhen(form.nextRunAt)}
                            </div>
                            {form.lastError && (
                                <div className="no-error-banner">Last check failed: {form.lastError}</div>
                            )}

                            {log && log.length > 0 && (
                                <div className="form-field">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <label className="form-label">Recent activity</label>
                                        <button className="no-link-btn" onClick={() => setFullLogOpen(true)}>View full log →</button>
                                    </div>
                                    <div className="no-log-list">
                                        {log.map(l => (
                                            <div key={l.id} className={`no-log-entry kind-${l.kind}`}>
                                                <strong>{new Date(l.createdAt).toLocaleString()}</strong> — {l.detail}
                                                {l.recipients && <span> → {l.recipients}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {notice && <div className="no-inline-note" style={{ color: '#1a7f37', fontWeight: 600, marginBottom: 'var(--space-2)' }}>{notice}</div>}
                            {error && <div className="no-error-banner">{error}</div>}
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => action.mutate('test')} disabled={action.isPending}>Send test</button>
                    <button className="btn btn-secondary" onClick={() => action.mutate('run')} disabled={action.isPending}>Run check now</button>
                    <span style={{ flex: 1 }} />
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !form}>
                        {save.isPending ? 'Saving…' : 'Save settings'}
                    </button>
                </div>
            </div>
            {fullLogOpen && (
                <FullLogModal storeId={store.storeId} storeName={store.name}
                    authHeaders={authHeaders} onClose={() => setFullLogOpen(false)} />
            )}
        </div>
    )
}

// ── Full activity log ─────────────────────────────────────────────────

function FullLogModal({ storeId, storeName, authHeaders, onClose }: {
    storeId: string
    storeName: string
    authHeaders: Record<string, string>
    onClose: () => void
}) {
    const { data: log, isLoading } = useQuery<NoLogEntry[]>({
        queryKey: ['neverout-fulllog', storeId],
        queryFn: async () => {
            const res = await fetch(API_BASE + `/api/neverout/stores/${storeId}/log?take=200`, { headers: authHeaders })
            if (!res.ok) throw new Error(await res.text())
            return res.json()
        },
    })
    return (
        <div className="modal-backdrop" style={{ zIndex: 60 }}>
            <div className="modal" style={{ maxWidth: 680 }}>
                <div className="modal-header">
                    <h2>Activity log — {storeName}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {isLoading ? (
                        <div className="no-inline-note">Loading…</div>
                    ) : !log || log.length === 0 ? (
                        <div className="no-inline-note">No activity recorded yet.</div>
                    ) : (
                        <>
                            <p className="no-inline-note" style={{ marginTop: 0 }}>
                                Showing the {log.length} most recent event{log.length === 1 ? '' : 's'}.
                            </p>
                            <div className="no-log-list" style={{ maxHeight: '55vh' }}>
                                {log.map(l => (
                                    <div key={l.id} className={`no-log-entry kind-${l.kind}`}>
                                        <strong>{new Date(l.createdAt).toLocaleString()}</strong> · {l.kind} — {l.detail}
                                        {l.recipients && <span> → {l.recipients}</span>}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    )
}
